import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';

export interface GrowthStageWarningDialogData {
  selectedCode: string;
  excludedCropFields: Array<{ cropFieldId: number; cropFieldName: string; currentCode: string }>;
  allowedCropFields: Array<{ cropFieldId: number; cropFieldName: string }>;
}

@Component({
  selector: 'app-growth-stage-warning-dialog',
  template: `
    <div class="dialog-container">
      <mat-dialog-content class="info-content">
        <div class="info-header">
          <span class="info-emoji">ℹ️🌿</span>
          <span class="info-text">{{ 'downyMildew.growthPhase.warning.infoLabel' | translate }}</span>
        </div>

        <p class="main-message">
          {{ getWarningMessage() }}
        </p>

        @if (data.allowedCropFields.length > 0) {
          <div class="section">
            <h3 class="section-title">{{ 'downyMildew.growthPhase.warning.willBeUpdated' | translate }}</h3>
            <ul class="field-list">
              @for (field of data.allowedCropFields; track field.cropFieldId) {
                <li>{{ field.cropFieldName }}</li>
              }
            </ul>
          </div>
        }

        @if (data.excludedCropFields.length > 0) {
          <div class="section">
            <h3 class="section-title">{{ 'downyMildew.growthPhase.warning.willNotBeUpdated' | translate }}</h3>
            <ul class="field-list">
              @for (field of data.excludedCropFields; track field.cropFieldId) {
                <li>{{ field.cropFieldName }}</li>
              }
            </ul>
          </div>
        }
      </mat-dialog-content>

      <mat-dialog-actions align="center">
        <button mat-raised-button color="accent" mat-dialog-close="continue" class="confirm-button" data-test="growth-stage-warning-ok">
          {{ 'downyMildew.growthPhase.warning.okGotIt' | translate }}
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [
    `
      .dialog-container {
        min-width: 450px;
      }

      mat-dialog-content {
        padding: 24px;
      }

      .info-content {
        padding: 24px 24px 16px 24px;
      }

      .info-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 16px;
      }

      .info-emoji {
        font-size: 20px;
      }

      .info-text {
        font-size: 12px;
        color: rgba(0, 0, 0, 0.6);
        font-weight: 500;
      }

      .main-message {
        margin: 0 0 16px 0;
        font-size: 14px;
        line-height: 1.5;
        color: rgba(0, 0, 0, 0.87);
      }

      .section {
        margin-bottom: 16px;
      }

      .section-title {
        margin: 0 0 8px 0;
        font-size: 13px;
        font-weight: 600;
        color: rgba(0, 0, 0, 0.87);
      }

      .field-list {
        margin: 0;
        padding-left: 20px;
        list-style-type: disc;
      }

      .field-list li {
        margin-bottom: 4px;
        font-size: 13px;
        color: rgba(0, 0, 0, 0.87);
      }

      mat-dialog-actions {
        padding: 16px 24px;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
      }

      .confirm-button {
        min-width: 150px;
      }
    `,
  ],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, AgrI18nSharedModule],
})
export class GrowthStageWarningDialogComponent {
  data = inject<GrowthStageWarningDialogData>(MAT_DIALOG_DATA);
  private readonly localeService = inject(LocaleService);


  getWarningMessage(): string {
    return this.localeService.translate('downyMildew.growthPhase.warning.message', {
      code: this.data.selectedCode,
    });
  }
}
